/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, ContextType, Ref, RefObject, JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useImperativeHandle, useMemo, useRef } from 'preact/hooks';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

import { Label } from '#UNSAFE_Label';
import { ReadonlyTextField, ReadonlyTextFieldInput, TextField } from '#UNSAFE_TextField';
import { TextFieldContent } from '#UNSAFE_TextField/TextFieldContent';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  type UserAssistanceDensityType
} from '#UNSAFE_UserAssistance';
import { type FocusableHandle, useFocusableTextField } from '#hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import type { FormVariantContext } from '#hooks/UNSAFE_useFormVariantContext';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useTextField } from '#hooks/UNSAFE_useTextField';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import type { BundleType } from '#resources/nls/bundle';
import { calendarDatesAreDifferent, translateCustomMasks } from '#utils/PRIVATE_dayUtils';
import { formatShortCalendarDate, getDatePlaceholderMasks } from '#utils/UNSAFE_calendarDateUtils';
import type { TextProps } from '#utils/UNSAFE_interpolations/text';
import type { Size } from '#utils/UNSAFE_size';
import type { LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import type { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { DateField } from './DateField';
import { useInputDateMaskContext } from './InputDateMaskContext';
import type { CalendarDate, DatePlaceholders } from './types';
import { useDateFieldState } from './useDateFieldState';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { dateReducer } from './dateReducer';
import { getDateFieldInitialReducerState } from './getDateFieldInitialReducerState';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type TextFieldContentProps = ComponentProps<typeof TextFieldContent>;

type Props = TestIdProps & {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];

  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];

  /**
   * Specifies how many columns to span in a FormLayout with direction === 'row'
   */
  columnSpan?: LayoutColumnSpan;

  /**
   * Use this property to provide content for the end slot, such as a calendar icon.
   */
  endContent?: TextFieldContentProps['endContent'];

  /**
   * Specifies the smallest date unit that is displayed by the component.
   * If set to month, only month and year are shown.
   * If set to day then month, day, and year are shown.
   */
  granularity?: 'month' | 'day';

  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];

  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];

  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;

  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * committed by the user. Setting this property to <code>true</code> means that a value
   * is required to be committed by the user.
   */
  isRequired?: boolean;

  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];

  /**
   * Specifies the label associated with the field.
   */
  label: string;

  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';

  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;

  /**
   * A ref to the component field. This can be used when you are composing with InputDateMask
   * and need to provide an anchor ref to a floating component.
   */
  mainFieldRef?: RefObject<HTMLDivElement>;

  /**
   * If specified, overrides the locale specific order of the individual date segments as
   * well as the locale specific separator. The value of each placeholder is only used
   * when the type is 'literal', otherwise it is replaced with a translated placeholder
   * for month, day, or year. If masks are specified and granularity = day, then the
   * placeholders must include month, day, and year. If granularity = month, then the
   * placeholders must include month and year, but not day.
   */
  masks?: DatePlaceholders;

  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];

  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextProps['textAlign'];

  /**
   * Specifies the density of the user assistance presentation. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;

  /**
   * The current display value of the component.
   */
  value?: CalendarDate;

  /**
   * The style variant of the component.
   */
  variant?: ContextType<typeof FormVariantContext>;

  /**
   * Callback invoked when the user commits the entered value by blurring or hitting the enter key.
   */
  onCommit?: (detail: ValueUpdateDetail<CalendarDate>) => void;

  /**
   * Callback invoked each time the user changes the value of a segment For example, if the user
   * types '12' into the month segment, this callback will be called twice to change the month
   * first to '1' and then '12'.
   */
  onInput: (detail: ValueUpdateDetail<CalendarDate>) => void;
};

/**
 * An InputDateMask allows you to edit the month, day, and year fields of a calendar date.
 */
export const InputDateMask = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      columnSpan,
      endContent,
      granularity = 'day',
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired,
      isRequiredShown,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      mainFieldRef,
      messages,
      masks,
      testId,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant,
      onCommit,
      onInput
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    // JET-72775 - Form control alignment in tables
    // default to 'start' if no text align is set
    const textAlign = propTextAlign ?? formTextAlign ?? 'start';
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;
    const enabledElementRef = useRef<HTMLDivElement>(null);
    const readonlyElementRef = useRef<HTMLDivElement>(null);

    const aRef = useRef<HTMLDivElement>(null);
    const anchorRef = mainFieldRef ?? aRef;

    const { isDropdownOpen, isInputDatePickerParent } = useInputDateMaskContext();

    const {
      focusProps,
      isFocused,
      methods: focusMethods
    } = useFocusableTextField<HTMLDivElement, HTMLDivElement>({
      isDisabled,
      isReadonly,
      enabledElementRef,
      readonlyElementRef
    });

    useImperativeHandle(ref!, () => focusMethods, [focusMethods]);

    const isComponentFocused = isFocused || (isInputDatePickerParent && isDropdownOpen);

    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps: origLabelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused: isComponentFocused,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value
    });

    // When using a div with role=group (i.e. DateField), you need aria-labelledby instead of a
    // for-id association with a label. Remove forId and id and use aria-labelledby instead.
    const { forId, ...labelProps } = origLabelProps;
    const { id, onBlur: inputPropsOnBlur, ...dateFieldProps } = inputProps;

    const ariaLabelledBy = labelProps.id;
    // Because we don't use for-id, clicking the label does not automatically focus the field.
    // We have to do it manually.
    const onClick = useCallback(() => {
      enabledElementRef.current?.focus();
    }, [enabledElementRef]);

    const labelComp =
      labelEdge !== 'none' ? (
        <Label {...(!isDisabled && !isReadonly ? { onClick } : {})} {...labelProps}>
          {label}
        </Label>
      ) : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined,
      labelText: labelEdge === 'inside' && !isDisabled ? label : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const hasInsideLabel = label !== undefined && labelEdge === 'inside';
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : isReadonly ? (
        <InlineUserAssistance
          fieldLabel={label}
          messages={messages}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    const { locale } = useUser();

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const monthPlaceholder = translations.inputDateMask_monthPlaceholder();
    const dayPlaceholder = translations.inputDateMask_dayPlaceholder();
    const yearPlaceholder = translations.inputDateMask_yearPlaceholder();

    // The DatePlaceholders type already ensures the masks contain the right placeholders,
    // for either full calendar dates or month-year dates. This check ensures the masks,
    // if provided, match the granularity. E.g. for granularity=month, you should pass
    // month and year placeholders, and a separator.
    if (
      masks &&
      ((granularity === 'day' && masks.length !== 5) ||
        (granularity === 'month' && masks.length !== 3))
    ) {
      throw new Error('Specified masks must match the granularity');
    }

    const dateMasks = useMemo(() => {
      // Determine the locale specific placeholder masks and separators in the correct order
      // for the locale, unless a custom set of masks was passed in. Each mask represents a
      // placeholder for a segment of a date (i.e. mm/dd/yyyy). Memoize because we shouldn't
      // need to recreate this data often.
      const placeholderMasks = masks
        ? translateCustomMasks(masks, monthPlaceholder, dayPlaceholder, yearPlaceholder)
        : getDatePlaceholderMasks(
            locale,
            granularity,
            monthPlaceholder,
            dayPlaceholder,
            yearPlaceholder
          );

      return placeholderMasks;
    }, [masks, locale, granularity, dayPlaceholder, monthPlaceholder, yearPlaceholder]);

    const { state, dispatch } = useDateFieldState({
      dateMasks,
      granularity,
      locale,
      value
    });

    // Keep track of the last committed date value sent to onCommit.
    const lastCommittedDateRef = useRef<CalendarDate | undefined>(value);
    // lastDateRef is a reference to the last date value that was changed by the user and sent via onInput.
    // It is used to make sure we do not call onInput with the same value as the previous onInput.
    const lastDateRef = useRef<CalendarDate | undefined>(value);

    const onInputIfDifferent = useCallback(
      (detail: ValueUpdateDetail<CalendarDate>) => {
        if (calendarDatesAreDifferent(detail.value, lastDateRef.current)) {
          const frozenDate =
            detail.value !== undefined ? Object.freeze({ ...detail.value }) : detail.value;
          onInput?.({ value: frozenDate });
          lastDateRef.current = frozenDate;
        }
      },
      [onInput]
    );

    const onCommitIfDifferent = useCallback(
      (detail: ValueUpdateDetail<CalendarDate>) => {
        if (calendarDatesAreDifferent(detail.value, lastCommittedDateRef.current)) {
          onCommit?.({ value: detail.value });
          lastCommittedDateRef.current = detail.value;
        }
      },
      [onCommit]
    );

    // The state gets reset when the component's value does not equal the last known value set in onInput.
    if (calendarDatesAreDifferent(lastDateRef.current, value)) {
      const initialState = getDateFieldInitialReducerState({
        granularity,
        locale,
        value
      });
      dispatch({ actionType: 'reset', data: initialState });
      // If the value was programmatically changed, then update the
      // last changed and last committed date refs to stay in sync.
      lastDateRef.current = value;
      lastCommittedDateRef.current = value;
    }

    // JET-73689: When we get onFocusOut on the date field that is not due to
    // interaction with the dropdown, we call onInput and onCommit.
    // We do not want to commit if we are simply moving between segments.
    // We dispatch 'commitOnBlur' to clear any selected segments and mark non-empty segments as
    // complete.
    const onDateFieldFocusOut = useCallback(
      (event: FocusEvent) => {
        if (
          (!isInputDatePickerParent || !isDropdownOpen) &&
          !(event.currentTarget as HTMLElement).contains(event.relatedTarget as HTMLElement)
        ) {
          // This pattern ensures that rendered state (like segment isSelected) is updated via dispatch,
          // while also allowing us to immediately compute the resulting currentDate by re-reducing the state.
          // We then call onInput and onCommit with the new value within the same event handler,
          // ensuring these are not called within a useLayoutEffect or other lifecycle hook.
          // This allows the parent to receive the currentDate synchronously in response to user action
          // and will re-render InputDateMask with the updated currentDate as needed.
          dispatch({ actionType: 'commitOnBlur' });

          const updatedState = dateReducer(state, { actionType: 'commitOnBlur' });
          onInputIfDifferent({ value: updatedState.currentDate });
          onCommitIfDifferent({ value: updatedState.currentDate });
        }
      },
      [
        dispatch,
        isDropdownOpen,
        isInputDatePickerParent,
        onCommitIfDifferent,
        onInputIfDifferent,
        state
      ]
    );

    // merge the date field's onblurs.
    const { onBlur: handleDateFieldBlur } = useMemo(() => {
      return mergeProps({ onBlur: onDateFieldFocusOut }, { onBlur: inputPropsOnBlur });
    }, [inputPropsOnBlur, onDateFieldFocusOut]);

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            columnSpan={columnSpan}
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onFocusOut}
            onFocus={focusProps.onFocusIn}
            ref={anchorRef}
            testId={testId}
            mainContent={
              <ReadonlyTextFieldInput
                aria-describedby={dateFieldProps['aria-describedby']}
                aria-label={ariaLabel}
                aria-labelledby={labelProps.id}
                as="div"
                elementRef={readonlyElementRef}
                textAlign={textAlign}
                value={
                  value === undefined ? '' : formatShortCalendarDate(value, granularity, dateMasks)
                }
                hasEmptyLabel={label === '' && labelEdge === 'none'}
                hasInsideLabel={hasInsideLabel}
                onBlur={inputProps.onBlur}
                onFocus={inputProps.onFocus}
              />
            }
            {...fieldLabelProps}></ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    // The main content inside TextField is a DateField (not TextFieldInput) in this case.
    // We reuse the TextField building blocks and hooks, but recompose them.
    const mainContent = (
      <DateField
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        baseId={baseId}
        dispatch={dispatch}
        fieldRef={enabledElementRef}
        granularity={granularity}
        hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
        isRequired={isRequired}
        masks={dateMasks}
        onCommit={onCommitIfDifferent}
        onBlur={handleDateFieldBlur}
        onInput={onInputIfDifferent}
        state={state}
        textAlign={textAlign}
        value={value}
        {...dateFieldProps}
      />
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          columnSpan={columnSpan}
          compactUserAssistance={compactUserAssistance}
          endContent={endContent}
          inlineUserAssistance={inlineUserAssistance}
          mainContent={mainContent}
          onBlur={focusProps.onFocusOut}
          onFocus={focusProps.onFocusIn}
          mainFieldRef={anchorRef}
          testId={testId}
          {...textFieldProps}
          {...fieldLabelProps}
        />
      </FormFieldContext.Provider>
    );
  }
);
